import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton } from '@mui/material';

import UrlTextField from '~/components/atoms/UrlTextField';
import {
  StyledAddMoreContainer,
  StyledAddMoreIcons,
  StyledItem,
  StyledItemCenter,
  StyledItemLeft,
} from '~/components/organisms/editor/SocialBlock/styled';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { QR_CODE_BUILDER_CREATED_ID_PARAM_NAME } from '~/constants/qr-code';
import { SOCIAL_ICON_ITEMS } from '~/constants/socials';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import { COLORS } from '~/styles/colors';
import { isMaxLength } from '~/utils/common';

type SocialLink = {
  label?: string;
  value?: string;
  title?: string;
};

type SocialBlockProps = {
  setSocialLinks?: (links: SocialLink[]) => void;
  socialLinks?: SocialLink[];
  dispatchHandler?: () => void;
  renderLabel?: boolean;
  renderTextField?: () => JSX.Element;
  isDeleteDisabled?: boolean;
};

export default function SocialBlock({
  setSocialLinks,
  socialLinks,
  dispatchHandler,
  renderLabel = true,
  renderTextField,
  isDeleteDisabled,
}: SocialBlockProps) {
  const { t } = useTranslation();
  const [selectedIcons, setSelectedIcons] = useState(socialLinks);
  const [searchParams] = useSearchParams();

  const isDesktop = useSelector(getIsDesktop);

  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME) || null;

  const handleAddIcon = (newItem, index) => {
    setSelectedIcons((prevSelected) => {
      const result = [...prevSelected, { ...newItem, value: SOCIAL_ICON_ITEMS[index].value, title: '' }];

      setSocialLinks(result, 'add', result.length - 1);

      return result;
    });
  };

  const handleRemoveIcon = (indexToRemove) => {
    const freshIconsList = selectedIcons.filter((_, index) => index !== indexToRemove);

    setSelectedIcons(freshIconsList);
    setSocialLinks(freshIconsList, 'delete', indexToRemove);
  };

  const handleCurrentSocialValue = ({ value, field, onBlur, index }) => {
    if (onBlur) {
      setSocialLinks(selectedIcons, 'update', index);
    } else {
      const freshIconsList = selectedIcons.map((item, indexT) =>
        indexT === index ? { ...item, [field]: value } : item,
      );
      setSelectedIcons(freshIconsList);
    }
  };

  useEffect(() => {
    const previewIcons = [];

    socialLinks.forEach((link) => {
      const socialItem = SOCIAL_ICON_ITEMS.find(({ network }) => network.toLowerCase() === link.label?.toLowerCase());

      if (socialItem) {
        previewIcons.push(
          createdId ? { ...socialItem, value: link?.value, label: link?.label, title: link?.title } : socialItem,
        );
      }
    });

    if (createdId && dispatchHandler) {
      dispatchHandler(previewIcons);
    }
  }, []);

  return (
    <Accordion
      summary={t('basicTemplateLabels:socilaNet')}
      variant="child"
      hideBottomBorder
      details={
        <>
          {renderTextField && renderTextField()}
          {selectedIcons?.map((item, index) => {
            const inputs = (
              <StyledItemCenter>
                <UrlTextField
                  requireProtocol={false}
                  urlValue={item?.value}
                  placeholder={item.label}
                  defaultValue={socialLinks[index]?.value || item?.value}
                  onChange={(e) => {
                    handleCurrentSocialValue({
                      value: e.target.value,
                      field: 'value',
                      onBlur: false,
                      index: index,
                    });
                  }}
                  onBlur={({ isValid }) => isValid && handleCurrentSocialValue({ onBlur: true, index: index })}
                />
                {renderLabel && (
                  <TextInput
                    onChange={(e) => {
                      handleCurrentSocialValue({
                        value: e.target.value,
                        field: 'title',
                        onBlur: false,
                        index,
                      });
                    }}
                    fullWidth
                    defaultValue={socialLinks[index]?.title || item?.title}
                    inputProps={{ maxLength: 70 }}
                    helperText={isMaxLength(item?.title, 70)}
                    placeholder={t('common:someDescription')}
                    onBlur={() => handleCurrentSocialValue({ onBlur: true, index: index })}
                  />
                )}
              </StyledItemCenter>
            );

            const defaultItemData = SOCIAL_ICON_ITEMS.find(
              ({ network, label }) =>
                network?.toLowerCase() === item.network?.toLowerCase() ||
                label?.toLowerCase() === item.label?.toLowerCase(),
            );

            return (
              <Fragment key={index}>
                <StyledItem>
                  <StyledItemLeft>
                    <img src={defaultItemData?.icon} width={32} height={32} alt={item.label} />
                    <Text
                      variant="t16Regular"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'no-wrap', width: 112 }}
                    >
                      {defaultItemData?.label}
                    </Text>
                  </StyledItemLeft>
                  {isDesktop && inputs}

                  <IconButton
                    disabled={isDeleteDisabled}
                    onClick={() => handleRemoveIcon(index)}
                    sx={{ height: '40px', width: '40px' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </StyledItem>
                {!isDesktop && inputs}
              </Fragment>
            );
          })}
          <StyledAddMoreContainer>
            <Text variant="input15" sx={{ color: COLORS.black08, marginBottom: '16px' }}>
              {t('labels:addMore')}
            </Text>
            <StyledAddMoreIcons>
              {SOCIAL_ICON_ITEMS.map((item, index) => {
                return (
                  <IconButton key={item.label} onClick={() => handleAddIcon(item, index)}>
                    <img src={item.icon} width={32} height={32} alt={item.label} />
                  </IconButton>
                );
              })}
            </StyledAddMoreIcons>
          </StyledAddMoreContainer>
        </>
      }
      defaultExpanded
    />
  );
}
